import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Check, CircleDashed, HelpCircle, Target, Wrench } from 'lucide-react';
import { Badge, Button } from '../ui';
import { useStepNavigation } from './navigation';

/**
 * One step inside a chapter (spec §10.4).
 *
 * Two completion models sit side by side and are told apart on purpose:
 *
 *   - a step the backend can evidence (mission mastery, deck review state) has
 *     NO checkbox. Its state is a read-only claim with the evidence spelled
 *     out ("Durch Mastery belegt"), so nobody can tick their way to a
 *     competence statement (spec §3.3 point 4).
 *   - a step only the user can judge (read this, do this in your real
 *     environment) has a checkbox and is labelled as self-reported.
 *
 * State is never carried by colour alone (spec §12): every state also has a
 * glyph and a word.
 */

const KIND_ICON = {
  content: BookOpen,
  question: HelpCircle,
  mission: Target,
  practice: Wrench,
};

export function StepItem({ step, onToggle }) {
  const { t } = useTranslation();
  const openStep = useStepNavigation();
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const metaId = `${baseId}-meta`;

  const KindIcon = KIND_ICON[step.kind] || BookOpen;
  const kindLabel = t(`onboarding.kind.${step.kind}`);
  const stateLabel = step.completed ? t('onboarding.stepDone') : t('onboarding.stepOpen');
  const evidenceLabel =
    step.evidence === 'mastery'
      ? t('onboarding.verifiedByMastery')
      : step.evidence === 'review'
        ? t('onboarding.verifiedByReview')
        : step.evidence === 'self'
          ? t('onboarding.selfReported')
          : null;

  return (
    <li className="etch-path__step" data-kind={step.kind} data-state={step.completed ? 'done' : 'open'}>
      {/* The label wraps the box so the whole 44×44 cell toggles it, not just
          the 20px control the browser draws (spec §12). The accessible name
          comes from `aria-labelledby`, which outranks the wrapping label. */}
      {step.manual ? (
        <label className="etch-path__step-status" data-interactive="true">
          <input
            type="checkbox"
            className="etch-path__step-check"
            checked={step.completed}
            onChange={(event) => onToggle(step, event.target.checked)}
            aria-labelledby={titleId}
            aria-describedby={metaId}
          />
        </label>
      ) : (
        <div className="etch-path__step-status">
          <span className="etch-path__step-mark" data-state={step.completed ? 'done' : 'open'}>
            {step.completed ? <Check size={16} aria-hidden="true" /> : <CircleDashed size={16} aria-hidden="true" />}
            <span className="etch-visually-hidden">{stateLabel}</span>
          </span>
        </div>
      )}

      <div className="etch-path__step-body">
        <p className="etch-path__step-title" id={titleId}>
          {step.title}
        </p>
        {step.description && <p className="etch-path__step-description">{step.description}</p>}

        <p className="etch-path__step-meta" id={metaId}>
          <span className="etch-path__step-kind">
            <KindIcon size={14} aria-hidden="true" />
            {kindLabel}
          </span>
          <Badge tone={step.required ? 'brand' : undefined}>
            {step.required ? t('onboarding.required') : t('onboarding.optional')}
          </Badge>
          {step.counts && (
            <span className="etch-path__step-counts">
              {t('onboarding.progressOf', { completed: step.counts.learned, total: step.counts.total })}
            </span>
          )}
          {evidenceLabel && <span className="etch-path__step-evidence">{evidenceLabel}</span>}
        </p>
      </div>

      {step.to && step.actionKey && (
        <div className="etch-path__step-action">
          <Button
            variant={step.kind === 'mission' ? 'primary' : 'secondary'}
            size="compact"
            onClick={() => openStep(step)}
          >
            {t(step.actionKey)}
          </Button>
        </div>
      )}
    </li>
  );
}

export default StepItem;
